/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import guidebookContentStyle from "../guidebookContentStyle";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Scheidungskosten_700px.jpg";
import Typography from "@mui/material/Typography";
import { Box, Link as MuiLink, Paper } from "@mui/material";
import { gaSet } from "../../../services/googleAnalyticsService";

const GuidebookDivorceCosts = () => {
  const title = "Ratgeber - Scheidungskosten | Legalbird";

  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta
          name="description"
          content="Wir geben Ihnen eine Übersicht zur Berechnung von Scheidungskosten.
        In der Legalbird Servicewelt finden Sie zudem einen Scheidungskostenrechner."
        />
      </Helmet>
      <Paper id={"teaser"} sx={guidebookContentStyle.paper}>
        <TeaserSlogan
          slogan={"Wie viel kostet eine (einvernehmliche) Scheidung?"}
          backgroundImagePath={backgroundTeaser}
          height={300}
          headingColor={"#333333"}
        />
        <TextContainer>
          <p>
            Wir geben Ihnen eine Übersicht zur Berechnung von Scheidungskosten. Sie haben auf anderen Websites günstigere Preise entdeckt? Wir erklären ganz
            offen, warum wir keine falschen Versprechungen machen, sondern nur realistische Preise anzeigen möchten. Zusätzlich zu den hier erklärten Kosten für
            das Gericht und den Anwalt, können noch Kosten für den Unterhalt auf Sie zukommen. Alle Informationen hierzu finden Sie im Spezialartikel zum{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/unterhalt"}>
              Unterhalt
            </MuiLink>
            .
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Basis für die Scheidungskosten: der Gegenstandswert (oder Verfahrenswert)
          </Typography>
          <p>
            Wie viel das Einreichen und Durchführen einer Scheidung kostet, ist in Deutschland sehr klar geregelt. Die Scheidungskosten setzen sich aus
            Gerichts- und Anwaltskosten zusammen. Wie hoch diese Kosten sind, bestimmt der sogenannte Gegenstandswert Ihrer Scheidung. Die Höhe des
            Gegenstandswertes wird vom zuständigen Gericht festgesetzt.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Wie wird der Gegenstandswert berechnet?
          </Typography>
          <p>
            Für die Berechnung des Streitwerts wird das monatliche Nettoeinkommen von Ihnen und Ihrem Ehepartner zusammen gerechnet und auf 3 Monate
            hochgerechnet. Hinzu kommen Kosten für die Durchführung des{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/versorgungsausgleich/"}>
              Versorgungsausgleichs
            </MuiLink>
            (je nach Einkommen ca. 1000 € bis 2000 €).
          </p>
          <HighlightBox headline={"Scheidungskosten in Kürze"}>
            <ul>
              <li>Scheidungskosten = Gerichtsgebühr + Anwaltskosten</li>
              <li>Höhe von Gerichtsgebühr und Anwaltskosten sind abhängig vom Gegenstandswert</li>
              <li>Gegenstandswert = 3 x Nettoeinkommen der Ehepartner + Kosten für Versorgungsausgleich</li>
            </ul>
          </HighlightBox>
          <p>
            Wenn Sie alle Angaben zu Ihrem Nettoeinkommen und dem
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/versorgungsausgleich/"}>
              {" "}
              Versorgungsausgleich{" "}
            </MuiLink>
            gemacht haben, wird der Gegenstandswert von uns für Sie vorläufig geschätzt. Der tatsächliche Gegenstandswert wird dann vom Gericht während Ihres
            Scheidungstermins festgesetzt, er weicht jedoch meist kaum von unserer Berechnung ab.
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Gerichtsgebühr und Anwaltskosten
          </Typography>
          <p>
            <strong>Wichtig zu wissen: Nur der Antragsteller muss anwaltlich vertreten werden</strong> – wenn der Antragsgegner (sprich: Ihr Ehepartner)
            ebenfalls geschieden werden will und dem Scheidungsantrag zustimmt, benötigt der Ehepartner keinen Anwalt! Damit fallen dann die Kosten für einen
            zweiten Anwalt weg. Sollten Sie sich mit Ihrem Ehepartner insofern einig sein, können Sie auf diese Weise die Kosten teilen. Sie sparen sich dann
            also die Kosten für einen zweiten Anwalt und Ihre Scheidung wird nur halb so teuer, wie wenn Sie beide anwaltlich vertreten wären. Im
            Gerichtskostengesetz (GKG) und dem Gesetz über die Vergütung von Anwälten (RVG) sind die Gerichtskosten und Mindestgebühren für die Anwaltskosten
            genau festgelegt. <strong>Wir rechnen stets nur die Mindestgebühren nach RVG ab.</strong>
          </p>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Sie können sich eine Scheidung nicht leisten? Finanzielle Unterstützung möglich!
          </Typography>
          <p>
            Sie haben ein sehr geringes Einkommen und sind daher nicht sicher, ob Sie die Scheidung bezahlen können? In Deutschland gibt es die
            Verfahrenskostenhilfe, um geringer Verdienende bei Rechtsangelegenheiten finanziell zu unterstützen. Wie das funktioniert und wie wir Ihnen dabei
            helfen, erfahren Sie{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"/ratgeber/familienrecht/verfahrenskostenhilfe"}>
              hier
            </MuiLink>
            .
          </p>

          <Box sx={guidebookContentStyle.scrollToAnchor} id={"tricks"} />
          <HighlightBox headline={"Falsche Tricks bei der Darstellung von Scheidungskosten"}>
            <p>
              Sie haben auf anderen Websites günstigere Preise entdeckt als bei uns? Dies liegt vermutlich am folgenden Trick bei der Darstellung der
              Scheidungskosten, die die Kosten geringer erscheinen lassen, als sie tatsächlich sind:
              <br />
              <br />
              <strong>Reduktion des Gegenstandswerts von 20-30% wird fest eingerechnet</strong>
              <br />
              Viele Websites werben damit, dass sie für Sie bei Gericht einen Antrag auf Reduktion des Streitwerts um 25% stellen, da Ihre Scheidung
              einvernehmlich ist. Ob diesem Antrag zugestimmt wird, ist jedoch Entscheidung des Gerichts und erfahrungsgemäß wird dem Antrag jedoch{" "}
              <strong>äußerst selten</strong> zugestimmt. Wir zeigen Ihnen daher immer die Kosten für eine Scheidung ohne Reduktion an, damit Sie sich auf die
              richtigen Kosten einstellen können und später nicht nachzahlen müssen.
              <br />
              <br />
              <strong>Der einzige Trick, um mit Sicherheit viel zu sparen: einvernehmlich scheiden lassen!</strong>
              <br />
              Klären Sie alle wichtigen Themen zuvor mit Ihrem Partner ab, damit Sie sich einvernehmich scheiden lassen können - denn dann ist nur ein Anwalt
              nötig und Sie sparen die sehr hohen Kosten für einen weiteren Anwalt. Sollten Sie sich bei einigen Themen noch unsicher sein, dann helfen wir
              Ihnen gerne jederzeit dabei, auch diese möglichst einfach und günstig abzuklären!
            </p>
          </HighlightBox>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Ihre Scheidung
          </Typography>
          <p>
            Sie möchten sich professionell, kostengünstig und unkompliziert scheiden lassen? Dann starten Sie jetzt mit Ihrer Scheidung! Sie können Ihre
            Scheidungskosten jederzeit erneut in Ihrer persönlichen Servicewelt berechnen und in nur 5 Minuten alle Angaben zu Ihrem Scheidungsantrag eingeben.
          </p>

          <DoubleButton buttonText={"zum Scheidungsantrag"} buttonLink={"/servicewelt"} backText={"Zurück zum Ratgeber"} backLink={"/ratgeber/familienrecht"} />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookDivorceCosts;
